import { dataDogAddWarning } from '../logging/datadog';
import { LANTERN_ERROR_CODES } from '../../constants/error';
import selectors from '../../rootSelectors';

export const START_API_CALL = 'START_API_CALL';
export const API_CALL_SUCCEEDED = 'API_CALL_SUCCEEDED';
export const API_CALL_FAILED = 'API_CALL_FAILED';
export const START_RETRIABLE_GET = 'START_RETRIABLE_GET';
export const RETRIABLE_GET_COMPLETE = 'RETRIABLE_GET_COMPLETE';
export const RETRIABLE_GET_FAILED = 'RETRIABLE_GET_FAILED';
export const CLEAR_APP_ERROR = 'CLEAR_APP_ERROR';
export const CLEAR_SCREEN_ERROR = 'CLEAR_SCREEN_ERROR';
export const START_BACKGROUND_RETRY = 'START_BACKGROUND_RETRY';
export const STOP_BACKGROUND_RETRY = 'STOP_BACKGROUND_RETRY';
export const REPORT_APP_ERROR = 'REPORT_APP_ERROR';
export const SET_BACKGROUND_RETRY_ID = 'SET_BACKGROUND_RETRY_ID';
export const AUDIO_SAVING_ERROR = 'AUDIO_SAVING_ERROR';
export const CLEAR_AUDIO_SAVING_ERROR = 'CLEAR_AUDIO_SAVING_ERROR';

export const startApiCall = (endpoint, requestConfig) => ({
    type: START_API_CALL,
    endpoint,
    requestConfig,
});

export const apiCallSucceeded = (endpoint, requestConfig) => ({
    type: API_CALL_SUCCEEDED,
    endpoint,
    requestConfig,
});

export const apiCallFailed = (
    endpoint,
    requestConfig,
    message,
    appLevelErrorStatus = LANTERN_ERROR_CODES.UNKNOWN_ERROR,
) => ({
    type: API_CALL_FAILED,
    endpoint,
    requestConfig,
    message,
    appLevelErrorStatus,
});

export const startRetriableGet = retryGetRequestActionName => ({
    type: START_RETRIABLE_GET,
    retryGetRequestActionName,
});

export const retriableGetComplete = retryGetRequestActionName => ({
    type: RETRIABLE_GET_COMPLETE,
    retryGetRequestActionName,
});

export const retriableGetFailed = retryGetRequestActionName => ({
    type: RETRIABLE_GET_FAILED,
    retryGetRequestActionName,
});

export const clearAppError = () => ({
    type: CLEAR_APP_ERROR,
});

export const clearScreenError = () => ({
    type: CLEAR_SCREEN_ERROR,
});

export const startBackgroundRetry = () => ({
    type: START_BACKGROUND_RETRY,
});

export const stopBackgroundRetry = () => ({
    type: STOP_BACKGROUND_RETRY,
});

export const cancelBackgroundRetry = () => (dispatch, getStore) => {
    const retryId = selectors.getBackgroundRetryId(getStore());
    if (retryId) {
        clearTimeout(retryId);
    }
    dispatch(stopBackgroundRetry());
};

export const reportAppError = message => ({
    type: REPORT_APP_ERROR,
    message,
});

export const setBackgroundRetryId = id => ({
    type: SET_BACKGROUND_RETRY_ID,
    id,
});

export const noticeApiError = (statusText, statusCode, responseMessage = null) => dispatch => {
    let message = `${statusCode} ${statusText}`;
    if (responseMessage) {
        message += ` - ${responseMessage}`;
    }

    dispatch(reportAppError(message));
};

export const networkError = (endpoint, requestConfig, error) => dispatch => {
    dispatch(apiCallFailed(endpoint, requestConfig, error.message, error.statusCode));
    // TODO: pass error object and report it to NR
    dispatch(noticeApiError(error.message, error.statusCode));

    if (error.statusCode === LANTERN_ERROR_CODES.UNKNOWN_ERROR) {
        Object.assign(error, {
            message: `${error.statusCode} Unknown error occurred: ${error.message}`,
        });
        dataDogAddWarning('Network error', { error });
        // eslint-disable-next-line no-console
        console.error('Network error', error);
    }
};

export const apiResponseError = (endpoint, requestConfig, response = {}, error) => (
    dispatch,
    getStore,
) => {
    dispatch(apiCallFailed(endpoint, requestConfig, response.statusText, response.status));

    // STEAM-8604: don't read the response body here. It only fed
    // noticeApiError -> REPORT_APP_ERROR, which no reducer handles, and the old
    // `response.headers.get('content-type')` threw on malformed responses. The
    // message is just "{status} {statusText}" (the parsed body was read as
    // `responseObj.body`, which is always undefined), and reading it needlessly
    // consumed the response stream.
    dispatch(noticeApiError(response.statusText, response.status, null));

    const state = getStore();
    // STEAM-8604: guard `error` — it is undefined on the offline-result path.
    if (error && selectors.getAppLevelErrorStatus(state) === LANTERN_ERROR_CODES.UNKNOWN_ERROR) {
        Object.assign(error, {
            message: `${error.statusCode} Unknown error occurred: ${error.message}`,
        });
        dataDogAddWarning('API response error', { error });
        // eslint-disable-next-line no-console
        console.error('API response error', error);
    }
};

export const setAudioSavingError = ({ statusCode = LANTERN_ERROR_CODES.UNKNOWN_ERROR } = {}) => ({
    type: AUDIO_SAVING_ERROR,
    statusCode,
});

export const clearAudioSavingError = () => ({
    type: CLEAR_AUDIO_SAVING_ERROR,
});
